// SOK-02: motivo de un cobro fallido, para decirle al cliente qué pasó y qué hacer.
//
// La respuesta del cobro llega en varias formas:
//   - Getnet (POSAPI): el carro de getCheckout / checkCheckoutStatus, con el detalle en
//     `payment_summary.response.{responseCode, responseMessage}`.
//   - Transbank por cable (POS): plano, `{responseCode, responseMessage}`, o `{error}` si el tótem no
//     alcanzó el servicio local (`https://localhost:5001`).
//   - Comanda A2A: igual, pero en PascalCase (`ResponseCode`, `ResponseMessage`).
//   - Error HTTP de getCheckout: texto, a veces JSON con `message`.
//
// En los cobros cloud (Getnet, Mercado Pago) el dueño del motivo es kiosk-backend: lo clasifica y lo
// guarda en el carro como `payment_failure_reason`, y en un error de getCheckout lo manda en el cuerpo
// junto a `cart_id`. Si viene, se usa tal cual. Esta clasificación queda para Transbank por cable
// (que no pasa por el backend) y como respaldo cuando el backend no alcanzó a darlo.
//
// El código no basta: Getnet usa 1006 tanto para "Operacion Cancelada" como para "Tiempo de espera
// excedido", y -11 para rechazos de todo tipo. Por eso manda el mensaje, y el código solo se usa si el
// mensaje no calza con ninguna regla. Las reglas salen de los carros cancelados reales (fiza_cl con
// Getnet y castano_cl con Transbank, revisados el 2026-10-19); lo que no calce queda como 'unknown'.

export const PAYMENT_FAILURE_REASONS = [
  'timeout',
  'declined',
  'pin',
  'insufficient_funds',
  'card_error',
  'communication',
  'canceled_by_user',
  'unknown',
];

// Se revisan en orden y gana la primera: "Negada, pin invalido" es PIN antes que rechazo.
// Los mensajes se comparan en minúsculas, sin tildes y con espacios simples.
const MESSAGE_RULES = [
  // "Tiempo de espera excedido" (Getnet 1006).
  { reason: 'timeout', pattern: /tiempo de espera|tiempo agotado|timeout|timed out/ },
  // "Negada, pin invalido" (55, -11), "Negada, tentativa de pin excedida" (75).
  { reason: 'pin', pattern: /\bpin\b|clave/ },
  // "Negada, fondos insuficientes" (51, -11).
  { reason: 'insufficient_funds', pattern: /fondos insuficientes|saldo insuficiente|insufficient/ },
  // "Error lectura tarjeta" (Transbank 9), "Reintente insertando tarjeta" (98),
  // "Tarjeta Retirada" (99: la sacaron antes de tiempo).
  { reason: 'card_error', pattern: /lectura|reintente insertando|tarjeta retirada/ },
  // "Failed to fetch (localhost:5001)", "Unexpected end of JSON input" (servicio local de Transbank),
  // "Host no responde" (Transbank 2), "Error Comunicaciones" (-1, -11), "Emisor indisponible" (92),
  // "Error respuesta autorizador" (1015), "Error de sistema" (96),
  // "Getnet no registra esta venta" (-1: la venta no llegó al POS).
  {
    reason: 'communication',
    pattern: /failed to fetch|load failed|networkerror|network error|unexpected end of json|failed to connect|comunicacion|conexion|host no responde|no responde|indisponible|autorizador|error de sistema|no registra esta venta/,
  },
  // "Operacion Cancelada" / "Operación cancelada" (Getnet 1006), "Cancelado por POS",
  // "Transacción cancelada desde el POS" (Transbank 7). "Cancelado por Getnet Services" (2003) no
  // lo canceló el cliente, así que no entra aquí.
  { reason: 'canceled_by_user', pattern: /operacion cancelada|cancelad[ao] (desde|por) (el )?pos/ },
  // "Rechazado" (Transbank 1), "Negada, ..." (límites de retiro, tarjeta restricta/expirada/no
  // efectiva, no honrar...), "Declinada por tarjeta", "Cuenta cerrada", "Tarjeta no soportada"
  // (Transbank 6), "Transaccion duplicada" (57), "Bloqueado por el titular...".
  {
    reason: 'declined',
    pattern: /rechaz|negad|declin|denegad|no autorizad|no honrar|cuenta cerrada|tarjeta (no soportada|restricta|expirada|vencida|invalida|no efectiva)|bloquead|duplicada/,
  },
  // Errores del propio tótem o de configuración ("Invalid total amount", "Comercio Invalido",
  // "Transaccion Invalida"): el cliente no puede hacer nada distinto, mensaje genérico.
  { reason: 'unknown', pattern: /invalid total amount|invalid ticket|cart is empty|comercio invalido|transaccion invalida/ },
];

// Respaldo por código cuando el mensaje no sirve. Los códigos de cada proveedor se pisan entre sí
// (el 3 es "Comercio Invalido" en Getnet y "Conexión falló" en Transbank), así que van por separado.
const GETNET_CODE_REASONS = {
  51: 'insufficient_funds',
  54: 'declined',
  55: 'pin',
  61: 'declined',
  62: 'declined',
  65: 'declined',
  75: 'pin',
  92: 'communication',
  96: 'communication',
  98: 'card_error',
  1015: 'communication',
};

const TRANSBANK_CODE_REASONS = {
  1: 'declined',
  2: 'communication',
  3: 'communication',
  6: 'declined',
  7: 'canceled_by_user',
  9: 'card_error',
};

const normalize = (text) => String(text)
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const pick = (source, ...keys) => {
  if (!source || typeof source !== 'object') return null;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const toCode = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const reasonFromMessage = (message) => {
  if (!message) return null;
  const text = normalize(message);
  return MESSAGE_RULES.find((rule) => rule.pattern.test(text))?.reason || null;
};

const parseText = (text) => {
  try {
    const parsed = JSON.parse(text);
    return pick(parsed, 'message', 'error', 'responseMessage', 'ResponseMessage') || text;
  } catch {
    return text;
  }
};

const parseJson = (text) => {
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

// El carro (200) o el cuerpo de un error de getCheckout (500), según el caso.
const backendPayload = (data) => (typeof data === 'string' ? parseJson(data) : data && typeof data === 'object' ? data : null);

const backendReasonOf = (data) => {
  const reason = backendPayload(data)?.payment_failure_reason;
  return PAYMENT_FAILURE_REASONS.includes(reason) ? reason : null;
};

/**
 * Id del carro del cobro fallido: el `id` del carro que devolvió el backend, o el `cart_id` del cuerpo
 * de un error de getCheckout. Null si no se sabe (p. ej. la petición no llegó al backend).
 */
export function getFailedCartId(response) {
  const payload = backendPayload(response?.data);
  if (!payload) return null;
  return (typeof response.data === 'string' ? payload.cart_id : payload.id || payload.cart_id) || null;
}

// Saca código, mensaje y error de cualquiera de las formas de la respuesta. Solo esos tres datos:
// nada de tarjeta, montos ni el resto del carro.
const extractFailure = (data) => {
  if (typeof data === 'string') return { provider: null, code: null, message: null, error: data ? parseText(data) : null };
  if (!data || typeof data !== 'object') return { provider: null, code: null, message: null, error: null };

  const summary = pick(data, 'payment_summary', 'PaymentSummary') || pick(data, 'checkout_summary') || data;
  const nested = pick(summary, 'response', 'Response');
  const detail = nested || summary;

  return {
    // La forma anidada es la de Getnet; la plana, la de Transbank (y Comanda A2A).
    provider: nested ? 'getnet' : 'transbank',
    code: toCode(pick(detail, 'responseCode', 'ResponseCode')),
    message: pick(detail, 'responseMessage', 'ResponseMessage'),
    error: pick(summary, 'error', 'Error') || (summary !== data ? pick(data, 'error', 'Error') : null),
  };
};

/**
 * Clasifica un cobro fallido.
 * @param {{status?: boolean, data?: any, outcome?: string, httpStatus?: number} | null} response -
 *   lo que devuelve checkoutAndWaitStatusViaAPI.
 * @returns {{reason: string, source: 'backend'|'kiosk', raw: {code: number|null, message: string|null}}}
 *   `source` dice quién clasificó: si es 'kiosk', el carro todavía no tiene el motivo. `raw` es lo que
 *   se guarda para soporte: solo código y mensaje.
 */
export function classifyPaymentFailure(response) {
  const backendReason = backendReasonOf(response?.data);

  // KIO-03: el tótem se rindió esperando al POS.
  if (response?.outcome === 'timeout' && !backendReason) {
    return { reason: 'timeout', source: 'kiosk', raw: { code: null, message: 'Tiempo de espera del pago agotado' } };
  }

  const { provider, code, message, error } = extractFailure(response?.data);
  const raw = { code, message: message || error || null };
  if (backendReason) return { reason: backendReason, source: 'backend', raw };

  const kiosk = (reason) => ({ reason, source: 'kiosk', raw });

  const byMessage = reasonFromMessage(message) || reasonFromMessage(error);
  if (byMessage) return kiosk(byMessage);

  const codes = provider === 'getnet' ? GETNET_CODE_REASONS : TRANSBANK_CODE_REASONS;
  if (code !== null && codes[code]) return kiosk(codes[code]);

  // Un `error` sin mensaje del POS es que no hubo con quién hablar. Un HTTP 4xx de getCheckout es un
  // rechazo del pedido (no del pago), así que queda genérico; un 5xx o un corte, comunicación.
  if (error && typeof response?.data !== 'string') return kiosk('communication');
  if (typeof response?.data === 'string') {
    const httpStatus = response?.httpStatus;
    if (httpStatus && httpStatus >= 400 && httpStatus < 500) return kiosk('unknown');
    return kiosk('communication');
  }
  return kiosk('unknown');
}
